import { useEffect, useState, useContext } from "react";
import { useNotification } from "../contexts/NotificationContext";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpDown,
  Search,
  FolderOpen,
  Clock,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ChevronRight,
  Filter,
  Trash2,
  FileText,
  Upload,
  ClipboardCheck,
  HourglassIcon,
  Circle,
  XCircle,
  Ban,
  RotateCw
} from "lucide-react";
import LoginChecker from "../components/LoginCkecker";
import { LoginContext } from "../LoginContext";
import MyPagination from "../components/MyPagination";
import apiService from "../backend/api";
import { getBaseUrl } from "../config/apiConfig";
import useProjects from "../hooks/useProjects";
import useProjectStatusWS from "../utils/useProjectStatusWS";

// shadcn UI
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";

const statusMap = {
  // Initial/creation statuses
  created: { key: "created", color: "bg-slate-400", icon: Circle, labelKey: "projectsPage.statusCreated", variant: "secondary" },
  file_pending: { key: "file_pending", color: "bg-blue-400", icon: Upload, labelKey: "projectsPage.statusFilePending", variant: "info" },
  file_received: { key: "file_received", color: "bg-blue-500", icon: Upload, labelKey: "projectsPage.statusFileReceived", variant: "info" },
  // Processing statuses
  queued: { key: "queued", color: "bg-slate-500", icon: HourglassIcon, labelKey: "projectsPage.statusQueued", variant: "secondary" },
  uploading: { key: "uploading", color: "bg-blue-500", icon: Upload, labelKey: "projectsPage.statusUploading", variant: "info" },
  in_progress: { key: "in_progress", color: "bg-amber-500", icon: Loader2, labelKey: "projectsPage.statusInProgress", variant: "warning" },
  processing: { key: "processing", color: "bg-amber-500", icon: Loader2, labelKey: "projectsPage.statusProcessing", variant: "warning" },
  // Completion statuses
  review: { key: "review", color: "bg-purple-500", icon: ClipboardCheck, labelKey: "projectsPage.statusReview", variant: "info" },
  ready: { key: "ready", color: "bg-emerald-500", icon: CheckCircle2, labelKey: "projectsPage.statusReady", variant: "success" },
  completed: { key: "completed", color: "bg-emerald-500", icon: CheckCircle2, labelKey: "projectsPage.statusCompleted", variant: "success" },
  // Rejection status
  rejected: { key: "rejected", color: "bg-rose-400", icon: Ban, labelKey: "projectsPage.statusRejected", variant: "error" },
  // Error statuses
  failed: { key: "failed", color: "bg-rose-500", icon: XCircle, labelKey: "projectsPage.statusFailed", variant: "error" },
  aborted: { key: "aborted", color: "bg-orange-500", icon: Ban, labelKey: "projectsPage.statusAborted", variant: "warning" },
  problem: { key: "problem", color: "bg-rose-500", icon: AlertCircle, labelKey: "projectsPage.statusProblem", variant: "error" },
  // Legacy compatibility
  inprogress: { key: "inprogress", color: "bg-amber-500", icon: Loader2, labelKey: "projectsPage.statusInProgress", variant: "warning" },
  finished: { key: "finished", color: "bg-emerald-500", icon: CheckCircle2, labelKey: "projectsPage.statusFinished", variant: "success" },
};

const ProjectsPage = () => {
  const { showNotification, confirm } = useNotification();
  LoginChecker();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginInfo } = useContext(LoginContext);
  const location = useLocation();
  const queryClient = useQueryClient();

  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");

  const username = location.state?.username || loginInfo?.user?.username || loginInfo?.username;

  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get("page")) || 1;
  const [page, setPage] = useState(initialPage);

  useEffect(() => {
    setSearchParams({ page });
  }, [page, setSearchParams]);

  // Data Fetching with React Query
  const { data, isLoading: loading} = useProjects(page, username);
  const projects = data?.projects || [];
  const totalPages = data?.totalPages || 1;

  // Real-time status updates via WebSocket
  useProjectStatusWS(username);

  const handleDelete = async (e, projectId) => {
    e.stopPropagation();
    const confirmed = await confirm(t("projectsPage.confirmDelete") || "Sind Sie sicher, dass Sie dieses Projekt dauerhaft löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.");
    if (!confirmed) {
      return;
    }

    try {
      await apiService.deleteProject(projectId);

      await queryClient.invalidateQueries(["projects", username]);

      // If we deleted the only project on this page (and it's not the first page), go back
      if (projects.length === 1 && page > 1) {
        setPage(prev => prev - 1);
      }

      showNotification(t("projectsPage.deleteSuccess"), "success");
    } catch (err) {
      console.error("Error deleting project:", err);
      showNotification(t("projectsPage.deleteError"), "error");
    }
  };

  const handleProjectClick = (project) => {
    // In-progress projects should redirect to upload page to continue/monitor
    const inProgressStatuses = ["created", "file_pending", "file_received", "queued", "uploading", "in_progress", "processing", "review"];

    if (inProgressStatuses.includes(project.status)) {
      // Navigate to upload page with project info for session restoration
      navigate("/upload", {
        state: {
          resumeProject: project,
          fromProjects: true
        }
      });
    } else {
      // Finished projects (incl. "problem"-Scans, die per Retry-Button neu
      // gestartet werden können) gehen zur Detail-Seite
      navigate(`/projects/${project._id}`);
    }
  };

  // Retry — Backend setzt das Projekt von "problem" zurück auf "queued",
  // anschließend leitet das Frontend zur Upload-Seite weiter, damit der
  // User die WebSocket-Verbindung mit derselben Job-ID wieder aufnimmt.
  // Wenn das Python-Backend die extrahierten Bilder noch hat, läuft der
  // Worker direkt weiter; sonst muss der User neu aufnehmen.
  const handleRetry = async (project, e) => {
    if (e) e.stopPropagation();
    try {
      await apiService.request(`${getBaseUrl()}/api/python-backend/projects/${project.jobId}/retry`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      showNotification(t("projectsPage.retryStarted", "Scan wird erneut gestartet …"), "success");
      navigate("/upload", {
        state: {
          resumeProject: { ...project, status: 'queued' },
          fromProjects: true,
        },
      });
    } catch (err) {
      console.error("Retry failed:", err);
      showNotification(t("projectsPage.retryFailed", "Erneuter Start fehlgeschlagen: ") + (err.message || ''), "error");
    }
  };

  const sortedProjects = [...projects].sort((a, b) => {
    const dateA = new Date(a.dateProcessed || a.date || 0);
    const dateB = new Date(b.dateProcessed || b.date || 0);
    return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
  });

  const filteredProjects = sortedProjects.filter((project) => {
    const searchStr = (project.title || project.jobId || "").toLowerCase();
    const descriptionStr = (project.description || "").toLowerCase();
    const categoryStr = (project.category || "").toLowerCase();
    const term = searchTerm.toLowerCase();
    return searchStr.includes(term) || descriptionStr.includes(term) || categoryStr.includes(term);
  });

  if (loading) {
    return (
      <div className="flex flex-col gap-4 justify-center items-center min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 border-4 border-[#0f7699]/20 dark:border-[#4fc3f7]/20 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-[#0f7699] dark:border-[#4fc3f7] border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p className="text-[#0f7699] dark:text-[#4fc3f7] font-black animate-pulse tracking-widest uppercase text-xs">
          {t("projectsPage.loadingProjects")}
        </p>
      </div>
    );
  }

  if (projects.length === 0 && page === 1) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900 text-center p-8 transition-colors duration-300">
        <div className="w-32 h-32 bg-slate-200 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-8 border border-slate-300 dark:border-slate-700">
          <FolderOpen size={64} className="text-slate-400 dark:text-slate-600" />
        </div>
        <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-4">
          {t("projectsPage.noProjects") || "Keine Projekte gefunden"}
        </h2>
        <p className="text-slate-500 dark:text-slate-400 font-medium max-w-md mb-10 leading-relaxed">
          {t("projectsPage.noProjectsDesc") || "Sie haben noch keine Scans durchgeführt. Starten Sie Ihren ersten Scan in der Workstation."}
        </p>
        <button
          className="px-8 py-4 bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 rounded-2xl font-black text-lg shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center gap-3"
          onClick={() => navigate("/upload")}
        >
          <Search size={24} />
          {t("projectsPage.startFirstScan") || "Ersten Scan starten"}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 transition-colors duration-300">
      {/* Page Header - Full Width */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <div className="px-3 sm:px-6 lg:px-8 py-2 sm:py-5">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0">
              <FolderOpen className="text-[#0f7699] dark:text-[#4fc3f7]" size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl md:text-3xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
                {t("projectsPage.title")}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
                {t("projectsPage.subtitle")}
              </p>
            </div>

            {location.state?.username && (
              <div className="hidden md:flex items-center gap-3 px-4 py-2.5 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 border border-[#0f7699]/30 dark:border-[#4fc3f7]/30 rounded-xl">
                <span className="text-xs font-black text-[#0f7699] dark:text-[#4fc3f7] uppercase tracking-widest">Admin View:</span>
                <span className="text-slate-900 dark:text-white font-bold">{location.state.username}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto p-4 sm:p-6 md:p-12">
        {/* Controls Bar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mb-6 md:mb-10">
          <div className="lg:col-span-8 relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors z-10" size={18} />
            <Input
              type="text"
              placeholder={t("projectsPage.searchPlaceholder")}
              className="w-full h-14 bg-card border-2 border-border rounded-2xl pl-12 pr-6 text-foreground placeholder:text-muted-foreground focus-visible:ring-primary/20 transition-all font-medium text-base"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="lg:col-span-4 flex gap-4">
            <Button
               variant="outline"
               onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
               className="flex-1 h-14 bg-card border-2 border-border rounded-2xl px-6 hover:border-muted-foreground/50 transition-all group active:scale-95 flex items-center justify-center gap-3"
            >
              <ArrowUpDown size={18} className={`text-primary transition-transform duration-500 ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
              <span className="font-black text-xs uppercase tracking-widest text-foreground">
                {sortOrder === "desc" ? t("projectsPage.sortNewest") : t("projectsPage.sortOldest")}
              </span>
            </Button>
            <Button variant="outline" className="w-14 h-14 bg-card border-2 border-border rounded-2xl flex items-center justify-center text-muted-foreground hover:text-foreground transition-all">
              <Filter size={20} />
            </Button>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {filteredProjects.map((project) => {
            const projectStatus = project?.status || 'unknown';
            const status = statusMap[projectStatus] || { key: "unknown", color: "bg-muted", icon: AlertCircle, labelKey: "projectsPage.statusUnknown", variant: "secondary" };
            return (
              <div
                key={project._id}
                onClick={() => handleProjectClick(project)}
                className="bg-card rounded-2xl border border-border p-4 active:scale-[0.98] transition-all cursor-pointer shadow-sm"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 bg-slate-100 dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 flex-shrink-0 flex items-center justify-center">
                    {project.image ? (
                      <img src={project.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <FileText size={16} className="text-slate-400 dark:text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-foreground">{project.title || project.jobId || "Scan"}</p>
                    <p className="text-[10px] text-muted-foreground font-mono truncate">ID: {project._id.slice(-8)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border-border">
                      {project.category || "General"}
                    </Badge>
                    <Badge
                      variant={status.variant || 'secondary'}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-lg"
                    >
                      <status.icon size={10} className={['processing', 'inprogress', 'uploading', 'in_progress'].includes(projectStatus) ? 'animate-spin' : ''} />
                      <span className="text-[9px] font-black uppercase tracking-wider">{t(status.labelKey) || projectStatus}</span>
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-medium">
                      <Clock size={12} />
                      {project.dateProcessed
                        ? new Date(project.dateProcessed).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
                        : project.date
                          ? new Date(project.date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
                          : "N/A"}
                    </span>
                    {project.status === 'problem' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t("projectsPage.retry", "Erneut versuchen")}
                        className="w-9 h-9 rounded-full text-amber-500 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-all"
                        onClick={(e) => handleRetry(project, e)}
                      >
                        <RotateCw size={15} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-9 h-9 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-all"
                      onClick={(e) => handleDelete(e, project._id)}
                    >
                      <Trash2 size={15} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop Table View */}
        <Card className="hidden md:block rounded-[2.5rem] overflow-hidden border-border bg-card shadow-2xl">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="px-6 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-primary">{t("projectsPage.tableJobIdentifier")}</TableHead>
                  <TableHead className="px-6 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-primary">{t("projectsPage.tableCategory")}</TableHead>
                  <TableHead className="px-6 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-primary">{t("projectsPage.tableDescription")}</TableHead>
                  <TableHead className="px-6 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-primary">{t("projectsPage.tableProcessedDate")}</TableHead>
                  <TableHead className="px-6 py-6 text-[10px] font-black uppercase tracking-[0.2em] text-primary text-center">{t("projectsPage.tableStatus")}</TableHead>
                  <TableHead className="px-6 py-6 w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map((project) => {
                  const projectStatus = project?.status || 'unknown';
                  const status = statusMap[projectStatus] || { key: "unknown", color: "bg-muted", icon: AlertCircle, labelKey: "projectsPage.statusUnknown" };
                  return (
                    <TableRow
                      key={project._id}
                      className="group cursor-pointer border-border hover:bg-muted/30 transition-all"
                      onClick={() => handleProjectClick(project)}
                    >
                      <TableCell className="px-6 py-7">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 flex-shrink-0 flex items-center justify-center">
                            {project.image ? (
                              <img src={project.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <FileText size={16} className="text-slate-400 dark:text-slate-600" />
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="font-black text-foreground tracking-tight mb-1">{project.jobId || project.title}</span>
                            <span className="text-[10px] font-bold text-muted-foreground font-mono">ID: {project._id.slice(-8)}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-7">
                        <Badge variant="secondary" className="px-3 py-1 text-xs font-black uppercase tracking-wider border-border">
                          {project.category || "General"}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-6 py-7 max-w-xs truncate text-muted-foreground font-medium italic">
                        {project.description || t("projectsPage.noDescription")}
                      </TableCell>
                      <TableCell className="px-6 py-7">
                        <div className="flex items-center gap-2 text-foreground/80 font-bold">
                          <Clock size={14} className="text-muted-foreground" />
                          <span>
                            {project.dateProcessed
                              ? new Date(project.dateProcessed).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                              : project.date
                                ? new Date(project.date).toLocaleDateString()
                                : "N/A"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-7">
                        <div className="flex justify-center">
                          <Badge
                            variant={status.variant || 'secondary'}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/5 shadow-lg"
                          >
                            <status.icon size={14} className={['processing', 'inprogress', 'uploading'].includes(projectStatus) ? 'animate-spin' : ''} />
                            <span className="text-[10px] font-black uppercase tracking-wider">
                              {t(status.labelKey) || projectStatus}
                            </span>
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-7 text-right">
                        <div className="flex items-center justify-end gap-4">
                          {projectStatus === 'problem' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={t("projectsPage.retry", "Erneut versuchen")}
                              className="w-10 h-10 rounded-full text-amber-500 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-all"
                              onClick={(e) => handleRetry(project, e)}
                            >
                              <RotateCw size={18} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-10 h-10 rounded-full text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-all opacity-0 group-hover:opacity-100"
                            onClick={(e) => handleDelete(e, project._id)}
                          >
                            <Trash2 size={18} />
                          </Button>
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-all">
                            <ChevronRight size={20} />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Improved Pagination */}
        <div className="mt-6 md:mt-12 group">
          <MyPagination
            totalPages={totalPages}
            page={page}
            setPage={setPage}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;
